import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import PlayCircleIcon from "@mui/icons-material/PlayCircleFilled";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";

import Button from "../../components/Button";
import UserScheduledSlots from "../../components/UserScheduledSlots";
import { getTrainingModuleStatus, getTrainingDetails, getScheduleSlots } from "../../api/training.deprecated";
import TrainingModuleStatus from "../../components/TrainingModuleStatus";
import useUser from "../../hooks/useUser";
import { useMoodleCourseOpener } from "../../components/Moodle/useMoodleCourseOpener";

const convertdate = (date) => {
  if (date == null) return "";
  else return new Date(date).toLocaleDateString("de-CH");
};

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

const DetailsTraining = ({ match, history }) => {
  const { t } = useTranslation();
  const openMoodleCourse = useMoodleCourseOpener();
  const [refresh, setRefresh] = useState(1);
  const [training, setTraining] = useState([]);
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [trainingModuleStatus, setTrainingModuleStatus] = useState([]);
  const userId = parseInt(match.params.userId);
  const trainingId = match.params.trainingId;
  const me = useUser();

  useEffect(() => {
    (async () => {
      //const resTraining = await getTraining(trainingId);
      const resTraining = await getTrainingDetails(trainingId);
      const resSchedule = await getScheduleSlots(match.params.trainingId, match.params.userId);
      const trainingModuleStatus = await getTrainingModuleStatus(trainingId, userId);
      setTrainingModuleStatus(trainingModuleStatus);
      setTraining(resTraining);
      setScheduleSlots(resSchedule);
    })();
  }, [refresh]);

  const showBoughtIcon = me.id !== userId;
  const showPlayIcon = me.id === userId;

  return (
    <>
      <Typography variant="h4">{t("training_pages.progress")}</Typography>
      <Stack spacing={2}>
        {trainingModuleStatus === null ? (
          <Typography>{t("training_pages.nothing_to_show_not_enrolled")}</Typography>
        ) : (
          <>
            {training.is_e_learning === 1 && (
              <Box>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ m: 1 }}>
                    <TrainingModuleStatus status={trainingModuleStatus.elearning_status} />
                  </Box>
                  <Typography variant="h6">{t("training.e_learning_module")}</Typography>
                </div>
                <Typography>
                  {t("training_pages.online_course")}:
                  {showPlayIcon && (
                    <Button
                      title={t("training_pages.go_to_online_course")}
                      icon={<PlayCircleIcon />}
                      onClick={() => openMoodleCourse(training.e_learning_moodle, userId, training.name || "")}
                    />
                  )}
                  {showBoughtIcon && (
                    <Button
                      title={t("training_pages.course_ready_for_user")}
                      icon={<AssignmentTurnedInIcon />}
                      onClick={() => {}}
                    />
                  )}
                </Typography>
                <Paper elevation={0} />
              </Box>
            )}
            <Divider />

            {training.is_classroom === 1 && (
              <Box>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ m: 1 }}>
                    <TrainingModuleStatus status={trainingModuleStatus.classroom_status} />
                  </Box>
                  <Typography variant="h6">{t("training.classroom_module")}</Typography>
                </div>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell align="right">{t("training.value")}</TableCell>
                        <TableCell align="right">{t("training.type")}</TableCell>
                        <TableCell align="right">{t("training.passed")}</TableCell>
                        <TableCell align="right">{t("training_pages.evaluator")}</TableCell>
                        <TableCell align="right">{t("date")}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {training.classroom_attendance == 1 && (
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {t("training_pages.attendance")}
                          </TableCell>
                          <TableCell align="right">{training.classroom_resource_link}</TableCell>
                          <TableCell align="right">{training.classroom}</TableCell>
                          <TableCell align="right">
                            <TrainingModuleStatus status={trainingModuleStatus.classroom_attendance} />
                          </TableCell>
                          <TableCell align="right">{trainingModuleStatus.classroom_attendance_fullname}</TableCell>
                          <TableCell align="right">
                            {convertdate(trainingModuleStatus.classroom_attendance_date)}
                          </TableCell>
                        </TableRow>
                      )}

                      {training.classroom_check == 1 && (
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {t("training_pages.test")}
                          </TableCell>
                          <TableCell align="right">
                            {training.written_test == 1
                              ? t("training_pages.written_test")
                              : t("training_pages.online_test")}
                          </TableCell>
                          <TableCell align="right">
                            {training.written_test == 1 ? (
                              t("training_pages.upload_written_test")
                            ) : (
                              <Button
                                title={t("training_pages.go_to_online_test")}
                                icon={<PlayCircleIcon />}
                                onClick={() => openMoodleCourse(training.classroom_moodle, userId, training.name || "")}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <TrainingModuleStatus status={trainingModuleStatus.classroom_test} />
                          </TableCell>
                          <TableCell align="right">{trainingModuleStatus.classroom_test_fullname}</TableCell>
                          <TableCell align="right">{convertdate(trainingModuleStatus.classroom_test_date)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            <Divider />

            {training.is_practical === 1 && (
              <>
                <Box>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ m: 1 }}>
                      <TrainingModuleStatus status={trainingModuleStatus.practical_status} />
                    </Box>

                    <Typography variant="h6">{t("training.practical_module")}</Typography>
                  </div>
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>

                        <TableCell align="right">{t("training.passed")}</TableCell>
                        <TableCell align="right">{t("training_pages.evaluator")}</TableCell>
                        <TableCell align="right">{t("date")}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {training.instructor_complete == 1 && (
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {t("training_pages.instructor_sign_off")}
                          </TableCell>
                          <TableCell align="right">
                            <TrainingModuleStatus status={trainingModuleStatus.instructor_complete} />
                          </TableCell>
                          <TableCell align="right">{trainingModuleStatus.instructor_fullname}</TableCell>
                          <TableCell align="right">
                            {convertdate(trainingModuleStatus.instructor_complete_date)}
                          </TableCell>
                        </TableRow>
                      )}

                      {training.assessor_passed == 1 && (
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {t("training_pages.assessment_passed")}
                          </TableCell>
                          <TableCell align="right">
                            <TrainingModuleStatus status={trainingModuleStatus.assessor_passed} />
                          </TableCell>
                          <TableCell align="right">{trainingModuleStatus.assessor_fullname}</TableCell>
                          <TableCell align="right">{convertdate(trainingModuleStatus.assessor_passed_date)}</TableCell>
                        </TableRow>
                      )}

                      {training.checker_passed == 1 && (
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {t("training_pages.check_passed")}
                          </TableCell>
                          <TableCell align="right">
                            <TrainingModuleStatus status={trainingModuleStatus.checker_passed} />
                          </TableCell>
                          <TableCell align="right">{trainingModuleStatus.checker_fullname}</TableCell>
                          <TableCell align="right">{convertdate(trainingModuleStatus.checker_passed_date)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            <Divider />

            {trainingModuleStatus.practical_status === 0 && (
              <Box>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="h6">{t("training_pages.schedule_slots")}</Typography>
                  <UserScheduledSlots scheduleSlots={scheduleSlots} />
                </div>
              </Box>
            )}
          </>
        )}
        <Button title={t("navigate_back")} type="button" onClick={() => history.goBack()}>
          {t("training_pages.back_to_overview")}
        </Button>
      </Stack>
    </>
  );
};

export default DetailsTraining;
